import { useState } from 'react';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import { useMutation } from '@tanstack/react-query';
import toast from 'react-hot-toast';
import { api } from '@/lib/api';
import { useAuthStore } from '@/stores/auth';
import { Card } from '@/components/ui/Card';
import { Button } from '@/components/ui/Button';
import { Input } from '@/components/ui/Input';

const profileSchema = z.object({
  firstName: z.string().min(1, 'First name is required'),
  lastName: z.string().min(1, 'Last name is required'),
  phone: z.string().optional(),
  handicap: z.number().min(-10).max(54).optional().nullable(),
});

type ProfileForm = z.infer<typeof profileSchema>;

export function AccountPage() {
  const { user, updateUser } = useAuthStore();
  const [activeTab, setActiveTab] = useState('profile');

  const { register, handleSubmit, formState: { errors, isDirty } } = useForm<ProfileForm>({
    resolver: zodResolver(profileSchema),
    defaultValues: {
      firstName: user?.firstName || '',
      lastName: user?.lastName || '',
      phone: user?.phone || '',
      handicap: user?.handicap || null,
    },
  });

  const updateMutation = useMutation({
    mutationFn: (data: ProfileForm) => api.user.update(data),
    onSuccess: (_, variables) => {
      updateUser(variables);
      toast.success('Profile updated successfully');
    },
    onError: () => {
      toast.error('Failed to update profile');
    },
  });

  return (
    <div className="bg-sand-50 min-h-screen py-8">
      <div className="container-wide">
        <h1 className="text-3xl font-bold text-sand-900 mb-8">Account Settings</h1>

        <div className="grid gap-8 lg:grid-cols-4">
          {/* Sidebar */}
          <div>
            <nav className="space-y-1">
              {[
                { id: 'profile', label: 'Profile', icon: 'M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z' },
                { id: 'notifications', label: 'Notifications', icon: 'M15 17h5l-1.405-1.405A2.032 2.032 0 0118 14.158V11a6.002 6.002 0 00-4-5.659V5a2 2 0 10-4 0v.341C7.67 6.165 6 8.388 6 11v3.159c0 .538-.214 1.055-.595 1.436L4 17h5m6 0v1a3 3 0 11-6 0v-1m6 0H9' },
                { id: 'security', label: 'Security', icon: 'M12 15v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2zm10-10V7a4 4 0 00-8 0v4h8z' },
              ].map((item) => (
                <button
                  key={item.id}
                  onClick={() => setActiveTab(item.id)}
                  className={`w-full flex items-center gap-3 px-4 py-3 text-sm font-medium rounded-xl transition-colors ${
                    activeTab === item.id
                      ? 'bg-sand-900 text-white'
                      : 'text-sand-700 hover:bg-sand-100'
                  }`}
                >
                  <svg className="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d={item.icon} />
                  </svg>
                  {item.label}
                </button>
              ))}
            </nav>
          </div>

          {/* Content */}
          <div className="lg:col-span-3">
            {activeTab === 'profile' && (
              <Card>
                <h2 className="text-lg font-semibold text-sand-900 mb-6">Profile Information</h2>

                <form onSubmit={handleSubmit((data) => updateMutation.mutate(data))} className="space-y-6">
                  <div className="grid gap-6 sm:grid-cols-2">
                    <Input
                      {...register('firstName')}
                      label="First Name"
                      error={errors.firstName?.message}
                    />
                    <Input
                      {...register('lastName')}
                      label="Last Name"
                      error={errors.lastName?.message}
                    />
                  </div>

                  <Input
                    type="email"
                    label="Email"
                    value={user?.email || ''}
                    disabled
                    className="bg-sand-50"
                  />

                  <Input
                    {...register('phone')}
                    label="Phone Number"
                    placeholder="+1 (555) 000-0000"
                    error={errors.phone?.message}
                  />

                  <Input
                    {...register('handicap', { valueAsNumber: true })}
                    type="number"
                    label="Handicap Index"
                    placeholder="e.g., 12.5"
                    step="0.1"
                    error={errors.handicap?.message}
                  />

                  <Button
                    type="submit"
                    variant="sage"
                    disabled={!isDirty}
                    isLoading={updateMutation.isPending}
                  >
                    Save Changes
                  </Button>
                </form>
              </Card>
            )}

            {activeTab === 'notifications' && (
              <Card>
                <h2 className="text-lg font-semibold text-sand-900 mb-6">Notification Preferences</h2>

                <div className="space-y-4">
                  {[
                    { id: 'email', label: 'Email Notifications', desc: 'Receive booking confirmations and reminders via email' },
                    { id: 'sms', label: 'SMS Notifications', desc: 'Get text message reminders for upcoming tee times' },
                    { id: 'marketing', label: 'Marketing Emails', desc: 'Receive special offers and promotions' },
                  ].map((item) => (
                    <label key={item.id} className="flex items-start gap-4 p-4 rounded-xl border border-sand-200 cursor-pointer hover:bg-sand-50 transition-colors">
                      <input
                        type="checkbox"
                        className="mt-1 h-5 w-5 rounded border-sand-300 text-sage-600 focus:ring-sage-500"
                      />
                      <div>
                        <p className="font-medium text-sand-900">{item.label}</p>
                        <p className="text-sm text-sand-500">{item.desc}</p>
                      </div>
                    </label>
                  ))}
                </div>

                <Button variant="sage" className="mt-6">
                  Save Preferences
                </Button>
              </Card>
            )}

            {activeTab === 'security' && (
              <Card>
                <h2 className="text-lg font-semibold text-sand-900 mb-6">Security</h2>

                <div className="space-y-6">
                  <div>
                    <h3 className="font-medium text-sand-900 mb-2">Change Password</h3>
                    <p className="text-sm text-sand-500 mb-4">Update your password to keep your account secure.</p>
                    <Button variant="outline">Change Password</Button>
                  </div>

                  <hr className="border-sand-200" />

                  <div>
                    <h3 className="font-medium text-sand-900 mb-2">Delete Account</h3>
                    <p className="text-sm text-sand-500 mb-4">Permanently delete your account and all associated data.</p>
                    <Button variant="outline" className="text-terracotta-600 border-terracotta-200 hover:bg-terracotta-50">
                      Delete Account
                    </Button>
                  </div>
                </div>
              </Card>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
